import { UserDataType, UserRoles, auth, pendingAuthState } from '../../firebaseApp';
import type { LoginNonceStatus } from '../store/store';

export const userHasAccessToPage = async (visibleOnlyToRoles: UserRoles[], userData: UserDataType | null): Promise<boolean> => {
  if (!visibleOnlyToRoles || visibleOnlyToRoles?.length === 0
      || userData?.roles?.includes(UserRoles.TeamMember)) {
    return true;
  } else if (auth.currentUser === null && visibleOnlyToRoles?.length > 0) {
    return false;
  } else if (auth.currentUser !== null && (visibleOnlyToRoles.includes(UserRoles.SignedInUser))) {
    return true;
  }
  // check if the user's roles matches any of the visibleOnlyToRoles
  return visibleOnlyToRoles?.some((role) => userData?.roles?.includes(role));
};

export const userHasAnyRole = (roles: UserRoles[], userData: UserDataType | null): boolean => {
  if (!roles || roles.length === 0) {
    return false;
  }
  // SignedInUser is satisfied by an authenticated session, like in userHasAccessToPage
  if (auth.currentUser !== null && roles.includes(UserRoles.SignedInUser)) {
    return true;
  }
  return roles.some((role) => userData?.roles?.includes(role));
};

/**
 * True while we still expect a session to arrive, so callers should wait rather
 * than commit to a signed-out or no-access state they would have to take back.
 *
 * Pass loginNonceStatus from the store: a nonce hand-off from the mobile app
 * signs out first and lands on browsers where userPreviouslyLoggedIn is false, so
 * it is the only signal that a session is coming.
 */
export const isUserDataStillHydrating = (userPreviouslyLoggedIn: boolean,
                                         userData: UserDataType | null,
                                         loginNonceStatus: LoginNonceStatus = 'idle') => {
  if (loginNonceStatus === 'redeeming') {
    return true;
  }
  return userPreviouslyLoggedIn && (pendingAuthState || auth.currentUser !== null) && userData === null;
};

export const isIos = () => {
  const ua = navigator.userAgent.toLowerCase();
  return ua.includes('iphone') || ua.includes('ipad')
    // @ts-expect-error userAgentData is not in the types yet (still experimental)
    || navigator.userAgentData?.platform?.toLowerCase().includes('ios');
};

export const isAndroid = () => {
  const ua = navigator.userAgent.toLowerCase();
  return ua.includes('android')
    // @ts-expect-error userAgentData is not in the types yet (still experimental)
    || navigator.userAgentData?.platform?.toLowerCase().includes('android');
};

const isMobile = () => {
  return isIos() || isAndroid();
};

export const isWindowsOs = () => {
  return navigator.userAgent.toLowerCase().includes('win64')
    // @ts-expect-error userAgentData is not in the types yet (still experimental)
    || navigator.userAgentData?.platform?.toLowerCase().includes('win')
    || navigator.platform?.toLowerCase().includes('win');
};

const isMacOs = () => {
  return navigator.userAgent.toLowerCase().includes('mac')
    // @ts-expect-error userAgentData is not in the types yet (still experimental)
    || navigator.userAgentData?.platform?.toLowerCase().includes('mac')
    || navigator.platform?.toLowerCase().includes('mac');
};

export const isSupportedDesktopOs = () => {
  return !isMobile() && (isWindowsOs() || isMacOs());
};

export function insertBetweenItems(arr, thing) {
  return [].concat(...arr.map((n) => [ n, thing ])).slice(0, -1);
}

export function createMeasureElement(text, insertElement) {
  const textWithoutBreaks = text
    .split('\n')
    .join('')
    .split('\r')
    .join('')
    .split(String.fromCharCode(10))
    .join('');

  const wordArray = textWithoutBreaks.split(' ');

  const alternatingArray = insertBetweenItems(wordArray, insertElement);

  return alternatingArray.join('');
}

export const isEmailAddress = (email: string): boolean => {
  const emailRegex = /\S+@\S+\.\S+/;
  return emailRegex.test(email);
};

export const parseName = (name: string): { firstName: string, lastName: string } => {
  const firstName = name.split(/\s+/)[0] || '';
  const lastName = name.split(/\s+/).slice(1).join(' ') || '';
  return { firstName, lastName };
};
